import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Video, Calendar as CalendarIcon, Clock, Plus, FileText, Users, Activity } from 'lucide-react';
import { format } from 'date-fns';
import { FloatingVideoCall } from '@/components/video/FloatingVideoCall';
import { NotesDialog } from '@/components/dashboard/NotesDialog';
import { WaitingRoom } from '@/components/video/WaitingRoom';
import { BookSessionDialog } from '@/components/customer/BookSessionDialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { watchUpcomingAppointments, type Appointment } from '@/lib/firebase/queries/appointments';
import { AvailabilityService } from '@/lib/services/availabilityService';
import { getUserById } from '@/lib/firebase/queries/users';
import { toast } from 'sonner';

interface OpenClinic {
  coachId: string;
  coachName: string;
  coachNameAr?: string;
  coachNameEn?: string;
  customersCount: number;
}

interface SessionWithCoach extends Appointment {
  coachName: string;
}

export function CoachingPage() {
  const { t, language } = useLanguage();
  const { currentUser, userData } = useAuth();
  const [selectedSession, setSelectedSession] = useState<SessionWithCoach | null>(null);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState<OpenClinic | null>(null);
  
  // Real data states
  const [upcomingSessions, setUpcomingSessions] = useState<SessionWithCoach[]>([]);
  const [pastSessions, setPastSessions] = useState<SessionWithCoach[]>([]);
  const [openClinics, setOpenClinics] = useState<OpenClinic[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingClinics, setLoadingClinics] = useState(true);

  // Fetch appointments
  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    const unsubscribe = watchUpcomingAppointments(
      currentUser.uid,
      'customer',
      async (appointments) => {
        try {
          const sessionsWithCoachNames = await Promise.all(
            appointments.map(async (appointment) => {
              const coach = await getUserById(appointment.coachId);
              const coachName = coach 
                ? (language === 'ar' 
                    ? `${coach.firstNameAr || ''} ${coach.lastNameAr || ''}`.trim()
                    : `${coach.firstNameEn || ''} ${coach.lastNameEn || ''}`.trim())
                : t('coaching.unknownCoach');
              
              return {
                ...appointment,
                coachName,
                date: appointment.date instanceof Date ? appointment.date : appointment.date.toDate()
              };
            })
          );

          const now = new Date();
          const upcoming = sessionsWithCoachNames.filter(s => 
            s.status === 'scheduled' || s.status === 'pending'
          );
          const past = sessionsWithCoachNames.filter(s => 
            s.status === 'completed' || s.status === 'cancelled'
          );

          setUpcomingSessions(upcoming);
          setPastSessions(past);
        } catch (error) {
          console.error('Error fetching coach details:', error);
          toast.error(t('coaching.errorFetchingAppointments'));
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error('Error in appointments listener:', error);
        toast.error(t('coaching.errorFetchingAppointments'));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid, language, t]);

  // Fetch open clinics
  useEffect(() => {
    setLoadingClinics(true);
    
    const unsubscribe = AvailabilityService.listenToOpenClinics((coaches) => {
      const clinics = coaches.map(coach => ({
        coachId: coach.coachId,
        coachName: language === 'ar' ? (coach.coachNameAr || coach.coachName) : (coach.coachNameEn || coach.coachName),
        coachNameAr: coach.coachNameAr,
        coachNameEn: coach.coachNameEn,
        customersCount: coach.customersCount
      }));
      setOpenClinics(clinics);
      setLoadingClinics(false);
    });

    return () => unsubscribe();
  }, [language]);

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const handleJoinClinic = (clinic: OpenClinic) => {
    setSelectedClinic(clinic);
    setShowWaitingRoom(true);
  };

  const handleCallStarted = (roomId: string) => {
    setShowWaitingRoom(false);
    // Navigate to video call with the roomId
    if (selectedClinic) {
      setSelectedSession({
        id: roomId,
        coachId: selectedClinic.coachId,
        coachName: selectedClinic.coachName,
        customerId: currentUser?.uid || '',
        date: new Date(),
        status: 'scheduled'
      } as SessionWithCoach);
      setShowVideoCall(true);
    }
  };

  const handleLeaveQueue = () => {
    setShowWaitingRoom(false);
    setSelectedClinic(null);
  };

  const customerName = userData 
    ? (language === 'ar' 
        ? `${userData.firstNameAr || ''} ${userData.lastNameAr || ''}`.trim()
        : `${userData.firstNameEn || ''} ${userData.lastNameEn || ''}`.trim())
    : t('videoCall.customer');

  // Show waiting room if selected
  if (showWaitingRoom && selectedClinic) {
    return (
      <WaitingRoom
        coachId={selectedClinic.coachId}
        coachName={selectedClinic.coachName}
        customerName={customerName}
        customerPhone={userData?.phone}
        onCallStarted={handleCallStarted}
        onLeaveQueue={handleLeaveQueue}
      />
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight mb-2">{t('coaching.title')}</h1>
        <p className="text-muted-foreground">
          {t('coaching.coachingDesc')}
        </p>
      </div>

      {/* Open Clinics Now Section */}
      <Card className="border-green-200 bg-green-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-green-600" />
            {t('coaching.openClinicsNow')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingClinics ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
            </div>
          ) : openClinics.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {openClinics.map((clinic) => (
                <motion.div
                  key={clinic.coachId}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Video className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-sm">{clinic.coachName}</h3>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Users className="h-3 w-3" />
                              <span>{clinic.customersCount} {t('coaching.waiting')}</span>
                            </div>
                          </div>
                        </div>
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          {t('coaching.open')}
                        </Badge>
                      </div>
                      <Button
                        onClick={() => handleJoinClinic(clinic)}
                        className="w-full bg-[#7dda55] hover:bg-[#6dc447] text-white"
                        size="sm"
                      >
                        <Video className="h-4 w-4 mr-2" />
                        {t('coaching.joinNow')}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('coaching.noClinicsOpen')}</p>
              <p className="text-sm mt-2">{t('coaching.checkBackLater')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sessions Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('coaching.yourSessions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="upcoming" className="w-full">
            <TabsList className="w-full grid grid-cols-2 mb-4">
              <TabsTrigger value="upcoming">{t('coaching.upcomingSessions')}</TabsTrigger>
              <TabsTrigger value="past">{t('coaching.pastSessions')}</TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming" className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : upcomingSessions.length > 0 ? (
                upcomingSessions.map((session, index) => {
                  const sessionIsToday = isToday(session.date);
                  const canJoin = sessionIsToday && session.status === 'scheduled';

                  return (
                    <motion.div
                      key={session.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card>
                        <CardContent className="p-6">
                          <div className="flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <h3 className="font-semibold">{session.coachName}</h3>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Badge variant="outline">
                                    {t(`coaching.sessionStatus.${session.status}`)}
                                  </Badge>
                                  <span className="flex items-center gap-1">
                                    <CalendarIcon className="h-3 w-3" />
                                    {format(session.date, 'PPP')}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {format(session.date, 'p')}
                                  </span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedSession(session);
                                    setShowNotes(true);
                                  }}
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  {t('coaching.notes')}
                                </Button>
                                {canJoin ? (
                                  <Button
                                    onClick={() => {
                                      setSelectedSession(session);
                                      setShowVideoCall(true);
                                    }}
                                    className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
                                  >
                                    <Video className="h-4 w-4 mr-2" />
                                    {t('coaching.joinCall')}
                                  </Button>
                                ) : (
                                  <Button variant="outline" disabled>
                                    <Clock className="h-4 w-4 mr-2" />
                                    {t('coaching.notYet')}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {t('coaching.noUpcomingSessions')}
                </div>
              )}
            </TabsContent>

            <TabsContent value="past" className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : pastSessions.length > 0 ? (
                pastSessions.map((session, index) => (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex flex-col gap-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <h3 className="font-semibold">{session.coachName}</h3>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Badge variant="outline">
                                  {t(`coaching.sessionStatus.${session.status}`)}
                                </Badge>
                                <span className="flex items-center gap-1">
                                  <CalendarIcon className="h-3 w-3" />
                                  {format(session.date, 'PPP')}
                                </span>
                                <Badge variant="secondary">
                                  {session.status}
                                </Badge>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setSelectedSession(session);
                                setShowNotes(true);
                              }}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              {t('coaching.notes')}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {t('coaching.noPastSessions')}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Button className="w-full" onClick={() => setShowBookingDialog(true)}>
        <Plus className="h-4 w-4 mr-2" />
        {t('coaching.bookSession')}
      </Button>

      <BookSessionDialog
        open={showBookingDialog}
        onOpenChange={setShowBookingDialog}
      />

      {showVideoCall && selectedSession && (
        <FloatingVideoCall
          roomId={selectedSession.id}
          userRole="customer"
          userName={customerName}
          otherParticipantName={selectedSession.coachName}
          onCallEnded={() => {
            setShowVideoCall(false);
            setSelectedSession(null);
            setSelectedClinic(null);
          }}
          open={showVideoCall}
          appointmentDate={selectedSession.date}
        />
      )}

      {showNotes && selectedSession && (
        <NotesDialog
          appointment={selectedSession}
          onOpenChange={(open) => {
            if (!open) {
              setShowNotes(false);
              setSelectedSession(null);
            }
          }}
        />
      )}
    </div>
  );
}
